/**
 * The style picker (spec 027): one full-screen surface for choosing a
 * story's look, opened by the first Generate of a story whose look is not
 * confirmed (the look gate) and by the Look row's "Edit look". A gallery of
 * the 12 ready-made styles, each with its shipped sample picture, then "Your
 * own style": the author describes a style, the image provider's text model
 * writes the look text and one sample is drawn (D-15, D-16), and the author
 * refines and draws again. The look text stays editable by hand either way.
 * It edits only this story's look (D-20): a story without one opens on the
 * look of the author's most recently confirmed story.
 *
 * The gallery works in every mode (its samples are static); describing and
 * previewing need image generation. Limits are protection, never an
 * allowance: no copy here says "free", "included" or "not charged".
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FALLBACK_LOOK_TEXT, LOOK_PALETTE_MAX, LOOK_PRESETS, LOOK_TEXT_MAX, LOOK_WISH_MAX, lookPreset, lookSampleUnitsNeeded,
  type Look, type LookOrigin, type LookPresetId, type StoryLookView,
  type ImageOwnerRef,
} from '../../../../Shared/src/images';
import { apiClient, type LookUpdate } from '../../api/client';
import { useImageGenStore } from '../../stores/imageGenStore';
import { limitReachedText, needsText, usedText } from './imageGenText';
import { LOOK_SAMPLE_URLS } from './lookSamples';
import styles from './ImageGen.module.scss';

export const LOOK_PICKER_TEXT = {
  gateTitle: 'Set the look before the first draft',
  gateIntro: 'Every picture in this story is drawn in one look. Pick a style, or describe your own.',
  editTitle: 'Edit look',
  editIntro: 'Pick a style, or describe your own. Pictures already made keep their look; Generate new redraws one in this look.',
  own: 'Your own style',
  ownSubtitle: 'describe it, preview it',
  edited: 'edited',
  lookText: 'Look text',
  palette: 'Palette',
  addColour: 'Add colour',
  removeColour: 'Remove last',
  use: 'Use this look',
  cancel: 'Cancel',
  saveFailed: "The look couldn't be saved. Try again.",
  describe: 'Describe the style you want',
  describePlaceholder: 'soft watercolour, warm light',
  writeAndDraw: 'Write and draw',
  refine: 'Refine',
  refinePlaceholder: 'more contrast',
  redraw: 'Redraw',
  drawAgain: 'Draw again',
  drawing: 'Writing the look and drawing a sample…',
  drawingSample: 'Drawing a sample…',
  textFailed: "The look couldn't be written. Try again or type it yourself.",
  sampleFailed: "The sample couldn't be drawn. Try again.",
  offNote: 'Describing and previewing needs image generation. Type the look text yourself.',
  costWish: "1 look text + 1 sample · 2 of this month's limit",
  costText: "1 sample · 1 of this month's limit",
} as const;

/** The card a look opens on: its preset when unedited, "Your own style" for own and edited looks, Flat vector without one. */
export type PickerChoice = LookPresetId | 'own';

export interface PickerDraft {
  choice: PickerChoice;
  text: string;
  palette: string[];
}

export function initialDraft(look: Pick<Look, 'presetId' | 'text' | 'palette'> | undefined): PickerDraft {
  if (!look) return { choice: 'flat', text: lookPreset('flat')?.text ?? FALLBACK_LOOK_TEXT, palette: [] };
  const preset = look.presetId && look.presetId !== 'own' ? lookPreset(look.presetId) : undefined;
  if (preset && preset.text === look.text) return { choice: preset.id, text: look.text, palette: look.palette ?? [] };
  return { choice: 'own', text: look.text, palette: look.palette ?? [] };
}

/** The `presetId` a draft saves with: the preset when its text is unchanged, `own` for "Your own style", none when a preset was edited. */
export function presetIdOf(draft: Pick<PickerDraft, 'choice' | 'text'>): LookOrigin | undefined {
  if (draft.choice === 'own') return 'own';
  return lookPreset(draft.choice)?.text === draft.text.trim() ? draft.choice : undefined;
}

/** The look the picker opens on: the story's, else the suggested one (its author's latest confirmed story), else none (Flat vector). */
export const openingLookOf = (view: StoryLookView | undefined): Look | undefined =>
  view?.look ?? view?.suggested;

const bodyOf = (draft: PickerDraft): LookUpdate => {
  const presetId = presetIdOf(draft);
  return { ...(presetId ? { presetId } : {}), text: draft.text.trim(), ...(draft.palette.length ? { palette: draft.palette } : {}) };
};

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** "Every picture in this story…" for a story; "…in this scene…" for a scene (spec 030 D-13). */
export const lookPickerGateIntro = (noun: 'story' | 'scene'): string =>
  (noun === 'scene' ? LOOK_PICKER_TEXT.gateIntro.replace('this story', 'this scene') : LOOK_PICKER_TEXT.gateIntro);

export interface LookPickerProps {
  /** The story (027); or give `owner` (spec 030). */
  storyId?: number;
  owner?: ImageOwnerRef;
  /** `gate`: opened by the first Generate (saving confirms and runs it); `edit`: the Look row's Edit look. */
  from: 'gate' | 'edit';
  onClose: () => void;
  /** `edit` only: after a save and the look's reload, whether the look text changed (the discard prompt, D-30). */
  onSaved?: (changed: boolean) => void;
}

export const LookPicker: React.FC<LookPickerProps> = ({ storyId, owner: given, from, onClose, onSaved }) => {
  const owner: ImageOwnerRef = given ?? { kind: 'story', id: storyId ?? 0 };
  const view = useImageGenStore((s) => s.look);
  const settings = useImageGenStore((s) => s.settings);
  const [draft, setDraft] = useState<PickerDraft>(() => initialDraft(openingLookOf(view)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // "Your own style": the wish, the refinement, the session's latest sample and the text it was drawn from.
  const [wish, setWish] = useState('');
  const [refine, setRefine] = useState('');
  const [sampleUrl, setSampleUrl] = useState<string | null>(null);
  const [sampleText, setSampleText] = useState<string | null>(null);
  const [drawing, setDrawing] = useState<null | 'wish' | 'text'>(null);
  const [ownError, setOwnError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);

  const canDraw = !!settings && settings.mode !== 'off' && settings.available;
  const left = settings?.limit.left ?? 0;

  useEffect(() => () => {
    abort.current?.abort();
  }, []);
  useEffect(() => () => {
    if (sampleUrl) {
      try { URL.revokeObjectURL(sampleUrl); } catch { /* jsdom */ }
    }
  }, [sampleUrl]);

  const cancel = useCallback(() => {
    abort.current?.abort();
    onClose();
  }, [onClose]);

  // Focus inside on open; Escape is Cancel; Tab stays inside.
  useEffect(() => {
    const root = rootRef.current;
    root?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
        return;
      }
      if (e.key !== 'Tab' || !root) return;
      const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || active === root)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [cancel]);

  const select = (choice: PickerChoice) => {
    setError(null);
    if (choice === 'own') setDraft((d) => ({ ...d, choice }));
    else setDraft((d) => ({ ...d, choice, text: lookPreset(choice)!.text }));
    if (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 599px)').matches) {
      detailRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    }
  };

  const draw = async (body: { wish?: string; text?: string }) => {
    const ctl = new AbortController();
    abort.current?.abort();
    abort.current = ctl;
    setDrawing(body.wish ? 'wish' : 'text');
    setOwnError(null);
    try {
      const res = owner.kind === 'scene'
        ? await apiClient.sampleSceneLook(owner.id, body, { signal: ctl.signal })
        : await apiClient.sampleLook(owner.id, body, { signal: ctl.signal });
      if (ctl.signal.aborted) return;
      if (!res.success) {
        if (res.error === 'monthly_limit_reached') setOwnError(limitReachedText(res.monthly ?? settings?.limit.monthly ?? 0));
        else if (res.error === 'sample_failed') {
          if (typeof res.text === 'string' && res.text) setDraft((d) => ({ ...d, choice: 'own', text: res.text! }));
          setOwnError(LOOK_PICKER_TEXT.sampleFailed);
        } else if (res.error === 'look_text_failed') setOwnError(LOOK_PICKER_TEXT.textFailed);
        else setOwnError(LOOK_PICKER_TEXT.sampleFailed);
        return;
      }
      const { text, sampleId } = res.data;
      setDraft((d) => ({ ...d, choice: 'own', text }));
      setSampleText(text);
      setRefine('');
      try {
        const blob = await apiClient.getLookSampleBlob(sampleId, { signal: ctl.signal });
        if (!ctl.signal.aborted) setSampleUrl(URL.createObjectURL(blob));
      } catch {
        if (!ctl.signal.aborted) setOwnError(LOOK_PICKER_TEXT.sampleFailed);
      }
    } catch {
      if (!ctl.signal.aborted) setOwnError(LOOK_PICKER_TEXT.sampleFailed);
    } finally {
      if (abort.current === ctl) {
        abort.current = null;
        setDrawing(null);
        void useImageGenStore.getState().loadSettings();
      }
    }
  };

  const stopDrawing = () => {
    abort.current?.abort();
    abort.current = null;
    setDrawing(null);
  };

  const costOf = (withWish: boolean): { title: string; disabled: boolean } => {
    const needed = lookSampleUnitsNeeded(withWish);
    if (!settings || left < needed) return { title: needsText(needed, left), disabled: true };
    return { title: [withWish ? LOOK_PICKER_TEXT.costWish : LOOK_PICKER_TEXT.costText, usedText(settings)].join('\n'), disabled: false };
  };

  const use = async () => {
    if (!draft.text.trim() || drawing) return;
    setSaving(true);
    setError(null);
    try {
      if (from === 'gate') {
        const result = await useImageGenStore.getState().confirmLook({ ...bodyOf(draft), confirm: true });
        if (result === 'failed' && useImageGenStore.getState().gate) setError(LOOK_PICKER_TEXT.saveFailed);
        else if (!useImageGenStore.getState().gate) onClose();
        return;
      }
      const body = bodyOf(draft);
      const before = view?.look?.text;
      const res = owner.kind === 'scene' ? await apiClient.saveSceneLook(owner.id, body) : await apiClient.saveStoryLook(owner.id, body);
      if (!res.success) {
        setError(LOOK_PICKER_TEXT.saveFailed);
        return;
      }
      await useImageGenStore.getState().loadLook();
      onSaved?.(before !== body.text);
      onClose();
    } catch {
      setError(LOOK_PICKER_TEXT.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const preset = draft.choice === 'own' ? undefined : lookPreset(draft.choice);
  const edited = !!preset && preset.text !== draft.text.trim();
  const title = from === 'gate' ? LOOK_PICKER_TEXT.gateTitle : LOOK_PICKER_TEXT.editTitle;
  const wishCost = useMemo(() => costOf(true), [settings, left]); // eslint-disable-line react-hooks/exhaustive-deps
  const textCost = useMemo(() => costOf(false), [settings, left]); // eslint-disable-line react-hooks/exhaustive-deps
  const textChanged = draft.text.trim().length > 0 && draft.text.trim() !== (sampleText ?? '').trim();

  return (
    <div
      className={styles.picker}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      tabIndex={-1}
      ref={rootRef}
      data-testid="look-picker"
      data-from={from}
    >
      <header className={styles.pickerHead}>
        <h3>{title}</h3>
        <p className={styles.note}>{from === 'gate' ? lookPickerGateIntro(owner.kind) : LOOK_PICKER_TEXT.editIntro}</p>
      </header>

      <div className={styles.pickerBody}>
        <div className={styles.gallery} data-testid="look-gallery">
          {LOOK_PRESETS.map((p) => {
            const on = draft.choice === p.id;
            return (
              <button
                key={p.id}
                type="button"
                aria-pressed={on}
                className={`${styles.card} ${on ? styles.cardOn : ''}`}
                onClick={() => select(p.id)}
                data-testid={`look-card-${p.id}`}
              >
                <img src={LOOK_SAMPLE_URLS[p.id]} alt={`${p.name} sample`} loading="lazy" width={150} height={150} />
                <strong>{p.name}</strong>
                <small>{p.subtitle}</small>
                {on && edited && <span className={styles.edited}>{LOOK_PICKER_TEXT.edited}</span>}
              </button>
            );
          })}
          <button
            type="button"
            aria-pressed={draft.choice === 'own'}
            className={`${styles.card} ${draft.choice === 'own' ? styles.cardOn : ''}`}
            onClick={() => select('own')}
            data-testid="look-card-own"
          >
            {sampleUrl
              ? <img src={sampleUrl} alt={`${LOOK_PICKER_TEXT.own} sample`} width={150} height={150} />
              : <span className={styles.cardEmpty} aria-hidden="true" />}
            <strong>{LOOK_PICKER_TEXT.own}</strong>
            <small>{LOOK_PICKER_TEXT.ownSubtitle}</small>
          </button>
        </div>

        <div className={styles.detail} ref={detailRef} data-testid="look-detail">
          {preset && (
            <img className={styles.bigSample} src={LOOK_SAMPLE_URLS[preset.id]} alt={`${preset.name} sample`} data-testid="look-big-sample" />
          )}
          {draft.choice === 'own' && (
            <div className={styles.flow} data-testid="look-own">
              {sampleUrl
                ? <img className={styles.bigSample} src={sampleUrl} alt={`${LOOK_PICKER_TEXT.own} sample`} data-testid="look-own-sample" />
                : null}
              {!canDraw ? (
                <p className={styles.note} data-testid="look-own-off">{LOOK_PICKER_TEXT.offNote}</p>
              ) : drawing ? (
                <div className={styles.row}>
                  <p className={styles.status} role="status" data-testid="look-own-drawing">
                    <span className={styles.spinner} />
                    {drawing === 'wish' ? LOOK_PICKER_TEXT.drawing : LOOK_PICKER_TEXT.drawingSample}
                  </p>
                  <button type="button" className={styles.button} onClick={stopDrawing} data-testid="look-own-cancel">
                    {LOOK_PICKER_TEXT.cancel}
                  </button>
                </div>
              ) : (
                <>
                  {!sampleUrl ? (
                    <label className={styles.field}>
                      {LOOK_PICKER_TEXT.describe}
                      <input
                        type="text"
                        value={wish}
                        maxLength={LOOK_WISH_MAX}
                        placeholder={LOOK_PICKER_TEXT.describePlaceholder}
                        onChange={(e) => setWish(e.target.value)}
                        data-testid="look-wish"
                      />
                    </label>
                  ) : (
                    <label className={styles.field}>
                      {LOOK_PICKER_TEXT.refine}
                      <input
                        type="text"
                        value={refine}
                        maxLength={LOOK_WISH_MAX}
                        placeholder={LOOK_PICKER_TEXT.refinePlaceholder}
                        onChange={(e) => setRefine(e.target.value)}
                        data-testid="look-refine"
                      />
                    </label>
                  )}
                  <div className={styles.row}>
                    {!sampleUrl ? (
                      <button
                        type="button"
                        className={styles.primary}
                        disabled={wishCost.disabled || !wish.trim()}
                        title={wishCost.title}
                        data-tooltip={wishCost.title}
                        onClick={() => { void draw({ wish: wish.trim() }); }}
                        data-testid="look-write-draw"
                      >
                        {LOOK_PICKER_TEXT.writeAndDraw}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={styles.primary}
                        disabled={wishCost.disabled || !refine.trim()}
                        title={wishCost.title}
                        data-tooltip={wishCost.title}
                        onClick={() => { void draw({ wish: refine.trim(), ...(draft.text.trim() ? { text: draft.text.trim() } : {}) }); }}
                        data-testid="look-redraw"
                      >
                        {LOOK_PICKER_TEXT.redraw}
                      </button>
                    )}
                    {textChanged && (
                      <button
                        type="button"
                        className={styles.button}
                        disabled={textCost.disabled}
                        title={textCost.title}
                        data-tooltip={textCost.title}
                        onClick={() => { void draw({ text: draft.text.trim() }); }}
                        data-testid="look-draw-again"
                      >
                        {LOOK_PICKER_TEXT.drawAgain}
                      </button>
                    )}
                  </div>
                </>
              )}
              {ownError && <p className={styles.error} role="alert" data-testid="look-own-error">{ownError}</p>}
            </div>
          )}

          <label className={styles.field}>
            <span>
              {LOOK_PICKER_TEXT.lookText}
              {edited && <span className={styles.edited}> · {LOOK_PICKER_TEXT.edited}</span>}
            </span>
            <textarea
              value={draft.text}
              maxLength={LOOK_TEXT_MAX}
              disabled={!!drawing}
              onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
              data-testid="look-text"
            />
          </label>

          <div className={styles.field}>
            {LOOK_PICKER_TEXT.palette}
            <div className={styles.row} data-testid="look-palette-edit">
              {draft.palette.map((c, i) => (
                <input
                  key={i}
                  type="color"
                  value={c}
                  aria-label={`Palette colour ${i + 1}`}
                  onChange={(e) => setDraft((d) => ({ ...d, palette: d.palette.map((x, j) => (j === i ? e.target.value : x)) }))}
                />
              ))}
              {draft.palette.length < LOOK_PALETTE_MAX && (
                <button type="button" className={styles.button} onClick={() => setDraft((d) => ({ ...d, palette: [...d.palette, '#888888'] }))}>
                  {LOOK_PICKER_TEXT.addColour}
                </button>
              )}
              {draft.palette.length > 0 && (
                <button type="button" className={styles.link} onClick={() => setDraft((d) => ({ ...d, palette: d.palette.slice(0, -1) }))}>
                  {LOOK_PICKER_TEXT.removeColour}
                </button>
              )}
            </div>
          </div>

          {error && <p className={styles.error} role="alert" data-testid="look-picker-error">{error}</p>}
        </div>
      </div>

      <footer className={styles.pickerFoot}>
        <button type="button" className={styles.button} onClick={cancel} data-testid="look-picker-cancel">
          {LOOK_PICKER_TEXT.cancel}
        </button>
        <button
          type="button"
          className={styles.primary}
          disabled={saving || !!drawing || !draft.text.trim()}
          onClick={() => { void use(); }}
          data-testid="look-picker-use"
        >
          {LOOK_PICKER_TEXT.use}
        </button>
      </footer>
    </div>
  );
};

/** The look gate: the picker while a Generate waits on the look (the store's `gate`). */
export const LookGatePicker: React.FC = () => {
  const gate = useImageGenStore((s) => s.gate);
  const storyId = useImageGenStore((s) => s.storyId);
  const sceneId = useImageGenStore((s) => (s.owner?.kind === 'scene' ? s.owner.id : undefined));
  if (!gate) return null;
  if (sceneId !== undefined) return <LookPicker owner={{ kind: 'scene', id: sceneId }} from="gate" onClose={() => useImageGenStore.getState().cancelGate()} />;
  if (storyId === undefined) return null;
  return <LookPicker storyId={storyId} from="gate" onClose={() => useImageGenStore.getState().cancelGate()} />;
};

export default LookPicker;
